import React, { Fragment, useState } from "react";
import { useController, Controller } from "react-hook-form";
import { CCollapse } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import { Input } from "../../../../components/form/Input/Input";
import Dropdown from "../../../../components/multiSelectDropdown/multiSelectDropdown";

export const DataPermissions = ({
  allPermissions,
  dailyEmailsPermissions,
  control,
  register,
  canChangeEmails,
  canUpdateCorporateReports,
  canSendInactiveUsersEmail,
  canSendLocationComparisonDailyEmail,
  setValue,
  watch,
  roles,
  canChangeRole,
  disableRole,
  selectedRole
}) => {
  const customPermission = canSendInactiveUsersEmail
    ? { id: "send_inactive_users_email", name: "Send Inactive Users Email" }
    : null;

  const watchedValue = watch(customPermission?.id);

  const { field: permissions } = useController({ control, name: "permission_ids" });
  const { field: corporatePermissions } = useController({ control, name: "corporate_report_ids" });

  const [permissionTypeKey, setPermissionTypeKey] = useState("");

  const [currentGroupName, setCurrentGroupName] = useState("");

  const onPermissionTypeClick = (type) => {
    setPermissionTypeKey(type);

    if (type === permissionTypeKey) {
      setPermissionTypeKey("");
      setCurrentGroupName("");
    }
  };

  const onGroupPermissionClick = (e) => {
    let targetText = e.target.innerText;
    let label = e.target.parentElement.dataset.label;
    let groupName = label ? label + "-" + targetText : targetText;

    if (groupName === currentGroupName) {
      setCurrentGroupName("");
      return;
    }

    setCurrentGroupName(groupName);
  };

  const onPermissionClick = (e, state) => {
    e.stopPropagation();
    let checked = e.target.checked;
    let val = +e.target.value;

    if (!checked) {
      delete state.value[val];
      state.onChange(state.value);
      return;
    }

    state.onChange({ ...state.value, [val]: checked });
  };

  const handleCustomControl = (checked) => {
    if (customPermission) {
      setValue(customPermission.id, checked, { shouldDirty: true });
    }
  };

  const onAllClick = ({ e, groupParent, options, state }) => {
    let currentPermissions = options.map((p) => p.id);
    let checked = e.target.checked;

    if (groupParent === "notifications_emails_goals") {
      handleCustomControl(checked);
    }

    if (checked) {
      let ids = currentPermissions.reduce((acc, val) => ({ ...acc, [val]: true }), {});
      state.onChange({ ...state.value, ...ids });
    } else {
      for (let key in state.value) {
        if (currentPermissions.includes(+key)) {
          delete state.value[key];
        }
      }

      state.onChange(state.value);
    }
  };

  const isAllChecked = (options, state) => {
    let allPermissionIdsPerGroup = options.map((permission) => permission.id);
    return allPermissionIdsPerGroup.every((id) => state.value[id]);
  };

  const areAllEmailsChecked = (options, state) => {
    const isChecked = isAllChecked(options, state);
    return isChecked && (!customPermission || watchedValue);
  };

  const handleStyles = (permission, state) => {
    let isSeparate = permission.name === "Separate emails for each location";

    return {
      display: !isSeparate ? "block" : state.value[dailyEmailsPermissions[0].id] ? "block" : "none",
      marginLeft: isSeparate ? "20px" : "0px"
    };
  };

  const renderInnerControls = ({ id, groupParent, group, options, state }) => (
    <>
      {groupParent === "location_permissions" && (
        <p style={{ fontWeight: "bold" }}>
          Selected Locations: {options.filter((option) => state.value[option.id]).length}
        </p>
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        style={{ paddingLeft: "40px", paddingBottom: "15px", width: "100%" }}
      >
        <div className="form-check" key={group}>
          <input
            className="form-check-input"
            type="checkbox"
            value={group}
            id={id ? id : group}
            onChange={(e) => onAllClick({ e, groupParent, options, state })}
            checked={
              groupParent == "notifications_emails_goals"
                ? areAllEmailsChecked(options, state)
                : isAllChecked(options, state)
            }
          />
          <label className="form-check-label" htmlFor={id ? id : group}>
            Check All / Uncheck All
          </label>
        </div>

        {options.map((permission) => {
          if (
            permission.name === "Location Comparison Daily Email" &&
            !canSendLocationComparisonDailyEmail
          )
            return null;
          return (
            <div className="form-check" key={permission.id} style={handleStyles(permission, state)}>
              <input
                className="form-check-input"
                type="checkbox"
                value={permission.id}
                id={permission.id}
                onChange={(e) => onPermissionClick(e, state)}
                checked={state.value[permission.id] ?? false}
              />
              <label className="form-check-label" htmlFor={permission.id}>
                {permission.name}
              </label>
            </div>
          );
        })}

        {groupParent === "notifications_emails_goals" && renderCustomControls()}
      </div>
    </>
  );

  const renderCustomControls = () => (
    <div onClick={(e) => e.stopPropagation()}>
      {customPermission && (
        <div className="form-check">
          <input
            {...register(customPermission.id)}
            id={customPermission.id}
            className="form-check-input"
            type="checkbox"
          />
          <label className="form-check-label" htmlFor={customPermission.id}>
            {customPermission.name}
          </label>
        </div>
      )}
    </div>
  );

  const renderNestedGroups = (groupParent, hideInnerCaption, state) =>
    Object.keys(allPermissions[groupParent]).map((parent) => {
      let isParent = !Array.isArray(allPermissions[groupParent][parent]);

      if (isParent) {
        return (
          <div key={parent} data-label={parent}>
            <div style={{ marginLeft: "20px" }}>{parent}</div>

            {Object.keys(allPermissions[groupParent][parent]).map((group) => {
              let id = parent + "-" + group;
              return (
                <Fragment key={id}>
                  <div className="group__item" style={{ marginLeft: "20px" }}>
                    {group}
                  </div>
                  <CCollapse show={id === currentGroupName}>
                    {renderInnerControls({
                      id,
                      group,
                      groupParent,
                      options: allPermissions[groupParent][parent][group],
                      state
                    })}
                  </CCollapse>
                </Fragment>
              );
            })}
          </div>
        );
      }

      return hideInnerCaption ? (
        <Fragment key={parent}>
          {renderInnerControls({
            group: parent,
            groupParent,
            options: allPermissions[groupParent][parent],
            state
          })}
        </Fragment>
      ) : (
        <Fragment key={parent}>
          <div className="group__item" style={{ marginLeft: "20px" }}>
            {parent}
          </div>
          <CCollapse show={parent === currentGroupName}>
            {renderInnerControls({
              group: parent,
              groupParent,
              options: allPermissions[groupParent][parent],
              state
            })}
          </CCollapse>
        </Fragment>
      );
    });

  const renderGroups = (groupParent, hideInnerCaption) => {
    let state =
      groupParent === "reporting_portal_permissions" ? corporatePermissions : permissions;

    return (
      <div onClick={(e) => onGroupPermissionClick(e)}>
        <CCollapse show={groupParent === permissionTypeKey}>
          {Array.isArray(allPermissions[groupParent])
            ? renderInnerControls({
                group: groupParent,
                groupParent,
                options: allPermissions[groupParent],
                state
              })
            : renderNestedGroups(groupParent, hideInnerCaption, state)}
        </CCollapse>
      </div>
    );
  };

  return (
    <div className="light group">
      {canChangeRole && (
        <div className="users__section">
          <div className="users__dropdown">
            <p>Role</p>
            {disableRole ? (
              <>
                <Input
                  value={selectedRole ? selectedRole.name : "None selected"}
                  customProps={{
                    disabled: true,
                    "data-tooltip-id": "disabledRoleTooltip",
                    "data-tooltip-content": "Multi-Organization User - Role cannot be edited"
                  }}
                />
                <Tooltip id="disabledRoleTooltip" place="bottom" />
              </>
            ) : (
              <Controller
                control={control}
                name="role_id"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Dropdown
                      height={275}
                      haveOnSelectChange
                      options={roles}
                      selectedFilters={value}
                      numberDisplayed={0}
                      onSelectChange={(data) => onChange(data)}
                    />
                  );
                }}
              />
            )}
          </div>
          <div className="users__empty" />
          <div className="users__empty" />
        </div>
      )}
      <div className="group__item" onClick={() => onPermissionTypeClick("charts_permissions")}>
        Chart Permissions
      </div>
      {renderGroups("charts_permissions")}
      {canUpdateCorporateReports && (
        <>
          <div
            className="group__item"
            onClick={() => onPermissionTypeClick("reporting_portal_permissions")}
          >
            Reporting Portal Permissions
          </div>
          {renderGroups("reporting_portal_permissions")}
        </>
      )}

      <div className="group__item" onClick={() => onPermissionTypeClick("admin_permissions")}>
        Admin Permissions
      </div>
      {renderGroups("admin_permissions")}

      {canChangeEmails && (
        <>
          <div
            className="group__item"
            onClick={() => onPermissionTypeClick("notifications_emails_goals")}
          >
            Notifications / Emails / Goals
          </div>

          {renderGroups("notifications_emails_goals", true)}
        </>
      )}
    </div>
  );
};
